import React, { useCallback, useState, useMemo } from 'react';
import { Button } from '@scale/scaleui';
import { useOCPStore } from '../hooks/useOCPStore';
import apiFetch, { HTTPError, defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import randomstring from 'randomstring';
import { useAddUsersToFlow } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/api';
import { AccountType } from '@scale/shared/defaults';
import { useMutation } from '@tanstack/react-query-v4';
import { OUTLIER_FRONTEND_URL } from 'react-scale/lib/constants';

interface TestFunnelProps {
  signupFlowId?: string;
  onCancel?: () => void;
}
export const TestFunnelWithNewAccount = (props: TestFunnelProps) => {
  const [wasAccountCreated, setWasAccountCreated] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const { currentFlow } = useOCPStore(state => ({
    currentFlow: state.currentFlow,
  }));

  const { mutate: addUsersToFlow, isError: isAddToFlowError } = useAddUsersToFlow();

  const randomCredentials = useMemo(() => {
    const domain = '@outlier.ai';
    const emailPrefix = randomstring.generate({ length: 6, readable: true });
    const randomEmail = `test-${emailPrefix}${domain}`;
    const randomPassword = randomstring.generate({
      length: 12,
      charset: 'alphanumeric',
    });
    return {
      email: randomEmail,
      password: `${randomPassword}!`,
      accountType: AccountType.Expert,
    };
  }, []);

  const {
    mutate: onClickCreate,
    isLoading: createInProgress,
    data: createdAccountId,
  } = useMutation({
    mutationKey: [randomCredentials, currentFlow?._id],
    mutationFn: async () => {
      const newTestAccount = await apiFetch(null, `corp-api/scalers/test_accounts`, {
        method: 'POST',
        body: randomCredentials,
      });

      setWasAccountCreated(true);

      addUsersToFlow({
        userIds: [newTestAccount.id],
        flowId: currentFlow?._id,
        signupFlowId: props.signupFlowId,
        batchSize: 1,
      });

      return newTestAccount.id;
    },
    onError: (error: unknown) => {
      defaultApiFetchErrorHandler(error as HTTPError, { handleHttp: true });
    },
  });

  const copyToClipboard = useCallback(async (text: string, type: 'email' | 'password' | 'link') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'email') {
        setCopiedEmail(true);
        setTimeout(() => setCopiedEmail(false), 2000);
      } else if (type === 'password') {
        setCopiedPassword(true);
        setTimeout(() => setCopiedPassword(false), 2000);
      } else if (type === 'link') {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      }
    } catch (err) {
      defaultApiFetchErrorHandler(err, { handleHttp: false });
    }
  }, []);

  const dashboardLink = useMemo<string>(() => {
    if (!createdAccountId) {
      return '';
    }

    return `${OUTLIER_FRONTEND_URL}/login`;
  }, [createdAccountId]);

  return (
    <>
      <Button onClick={props.onCancel} variant="neutral">
        Cancel
      </Button>

      <div className="flex flex-col space-y-2 my-4">
        <div className="flex flex-row items-center justify-between">
          <div className="font-medium">Email:</div>
          <div className="flex items-center">
            <div className="mr-2">{randomCredentials.email}</div>
            <Button
              onClick={() => copyToClipboard(randomCredentials.email, 'email')}
              variant="neutral"
              size="sm"
            >
              {copiedEmail ? 'Copied!' : 'Copy'}
            </Button>
          </div>
        </div>
        <div className="flex flex-row items-center justify-between">
          <div className="font-medium">Password:</div>
          <div className="flex items-center">
            <div className="mr-2">•••••••••••••</div>
            <Button
              onClick={() => copyToClipboard(randomCredentials.password, 'password')}
              variant="neutral"
              size="sm"
            >
              {copiedPassword ? 'Copied!' : 'Copy'}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-row gap-2 items-center">
        <Button
          onClick={() => onClickCreate()}
          variant="primary"
          disabled={createInProgress || wasAccountCreated}
        >
          {createInProgress ? 'Creating...' : wasAccountCreated ? 'Account Created' : 'Create'}
        </Button>
        {wasAccountCreated && createdAccountId && <div>New user ID: {createdAccountId}</div>}
      </div>
      {wasAccountCreated && (
        <div className="flex flex-row items-center justify-between mt-4 mb-4">
          <div className="font-medium">Login Link (open in incognito):</div>
          <div className="flex items-center">
            <div className="mr-2 max-w-xs truncate">{dashboardLink}</div>
            <Button
              onClick={() => copyToClipboard(dashboardLink, 'link')}
              variant="neutral"
              size="sm"
            >
              {copiedLink ? 'Copied!' : 'Copy'}
            </Button>
          </div>
        </div>
      )}

      {isAddToFlowError && (
        <div className="mt-4 mb-4">
          The account could not be added to this onboarding flow. Please try again
        </div>
      )}
    </>
  );
};
